import { ValueLabelRecord } from '@blisspointmedia/bpm-types/dist/App2Performance';

export const DEFAULT_ALERT_TYPE = 'daily';
export const DEFAULT_ALERT_TIME = '10:00am';
export const DEFAULT_ALERT_DAY_OF_WEEK = 'Monday';
export const DEFAULT_ALERT_DAY_OF_MONTH = '1';
export const DEFAULT_SLACK_CHANNEL = '';
export const DEFAULT_SLACK_MEMBER = '';
export const DEFAULT_EMAIL_ADDRESS = '';

export interface AlertFormData {
  alertName: string;
  alertType: 'budget' | 'custom';
  budgetId: number;
  spendCadence: string;
  customSql: string;
  actual: {
    enabled: boolean;
    over: string;
    under: string;
    thresholdType: 'percentage' | 'dollar';
    budgetType: 'total' | 'ideal';
    alwaysNotify: boolean;
    frequency: {
      type: 'hourly' | 'daily' | 'weekly' | 'monthly';
      time: string;
      dayOfWeek: string;
      dayOfMonth: string;
    };
  };
  projected: {
    enabled: boolean;
    over: string;
    under: string;
    thresholdType: 'percentage' | 'dollar';
    alwaysNotify: boolean;
    frequency: {
      type: 'hourly' | 'daily' | 'weekly' | 'monthly';
      time: string;
      dayOfWeek: string;
      dayOfMonth: string;
    };
  };
  remainder: {
    enabled: boolean;
    over: string;
    under: string;
    thresholdType: 'percentage' | 'dollar';
    alwaysNotify: boolean;
    frequency: {
      type: 'hourly' | 'daily' | 'weekly' | 'monthly';
      time: string;
      dayOfWeek: string;
      dayOfMonth: string;
    };
  };
  slack: {
    enabled: boolean;
    channels: string[];
    members: string[];
  };
  email: {
    enabled: boolean;
    addresses: string[];
  };
  startDate: string;
  endDate: string;
  groupIsActive: boolean;
}

export const DEFAULT_ALERT_FORM_DATA: AlertFormData = {
  alertName: '',
  alertType: 'budget',
  budgetId: 0,
  spendCadence: 'Evenly distributed',
  customSql: '',
  actual: {
    enabled: true,
    over: '',
    under: '',
    thresholdType: 'percentage',
    budgetType: 'total',
    alwaysNotify: false,
    frequency: {
      type: DEFAULT_ALERT_TYPE,
      time: DEFAULT_ALERT_TIME,
      dayOfWeek: DEFAULT_ALERT_DAY_OF_WEEK,
      dayOfMonth: DEFAULT_ALERT_DAY_OF_MONTH
    }
  },
  projected: {
    enabled: true,
    over: '',
    under: '',
    thresholdType: 'percentage',
    alwaysNotify: false,
    frequency: {
      type: DEFAULT_ALERT_TYPE,
      time: DEFAULT_ALERT_TIME,
      dayOfWeek: DEFAULT_ALERT_DAY_OF_WEEK,
      dayOfMonth: DEFAULT_ALERT_DAY_OF_MONTH
    }
  },
  remainder: {
    enabled: true,
    under: '',
    over: '',
    thresholdType: 'percentage',
    alwaysNotify: false,
    frequency: {
      type: DEFAULT_ALERT_TYPE,
      time: DEFAULT_ALERT_TIME,
      dayOfWeek: DEFAULT_ALERT_DAY_OF_WEEK,
      dayOfMonth: DEFAULT_ALERT_DAY_OF_MONTH
    }
  },
  slack: {
    enabled: true,
    channels: [DEFAULT_SLACK_CHANNEL],
    members: [DEFAULT_SLACK_MEMBER]
  },
  email: {
    enabled: true,
    addresses: [DEFAULT_EMAIL_ADDRESS]
  },
  startDate: '',
  endDate: '',
  groupIsActive: true
};

export const THRESHOLD_TYPES = {
  PERCENTAGE: 'percentage',
  DOLLAR: 'dollar'
} as const;

export const FREQUENCY_TYPES = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly'
} as const;

export const SPEND_CADENCE_OPTIONS = {
  EVENLY_DISTRIBUTED: 'Evenly distributed',
  FRONT_LOADED: 'Front loaded',
  BACK_LOADED: 'Back loaded'
} as const;

/** A Dropdown option carrying the preset payload it represents. */
export interface PresetSelectOption extends ValueLabelRecord {
  presetData: AlertFormData;
}

export interface BudgetRow {
  id?: string | number;
  goal_name?: string;
  goal_value?: unknown;
  start_date?: string;
  end_date?: string;
  [key: string]: unknown;
}

export interface BudgetOption {
  value: string;
  label: string;
  rowData: BudgetRow | null;
}

export const FREQUENCY_TYPE_OPTIONS = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
] as const;

export const DAY_OF_WEEK_OPTIONS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '0', label: 'Sunday' }
] as const;

export const DAY_OF_MONTH_OPTIONS = [
  { value: '1', label: '1st' },
  { value: '2', label: '2nd' },
  { value: '3', label: '3rd' },
  { value: '4', label: '4th' },
  { value: '5', label: '5th' },
  { value: '6', label: '6th' },
  { value: '7', label: '7th' },
  { value: '8', label: '8th' },
  { value: '9', label: '9th' },
  { value: '10', label: '10th' },
  { value: '11', label: '11th' },
  { value: '12', label: '12th' },
  { value: '13', label: '13th' },
  { value: '14', label: '14th' },
  { value: '15', label: '15th' },
  { value: '16', label: '16th' },
  { value: '17', label: '17th' },
  { value: '18', label: '18th' },
  { value: '19', label: '19th' },
  { value: '20', label: '20th' },
  { value: '21', label: '21st' },
  { value: '22', label: '22nd' },
  { value: '23', label: '23rd' },
  { value: '24', label: '24th' },
  { value: '25', label: '25th' },
  { value: '26', label: '26th' },
  { value: '27', label: '27th' },
  { value: '28', label: '28th' },
  { value: '29', label: '29th' },
  { value: '30', label: '30th' },
  { value: '31', label: '31st' }
] as const;

export const CACHE_TIMES = {
  STALE_TIME: 5 * 60 * 1000,
  CACHE_TIME: 10 * 60 * 1000
} as const;

export const RETRY_CONFIG = {
  MAX_RETRIES: 2
} as const;

export const WINDOW_FOCUS_CONFIG = {
  REFETCH_ON_WINDOW_FOCUS: false
} as const;

export const DEFAULT_CACHE_CONFIG = {
  staleTime: CACHE_TIMES.STALE_TIME,
  gcTime: CACHE_TIMES.CACHE_TIME,
  retry: RETRY_CONFIG.MAX_RETRIES,
  refetchOnWindowFocus: WINDOW_FOCUS_CONFIG.REFETCH_ON_WINDOW_FOCUS
} as const;
